import React, { useEffect, useState } from "react";
import { ChevronDown, Moon, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { presenceMeta, STATUS_PRESETS } from "../../lib/presence";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";
import { api } from "../../lib/api";
import { Switch } from "../ui/switch";

function useInvisibleMode(enabled) {
  const { invisible: on, setInvisible: setOn } = useSocket();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (enabled) api.get("/presence/invisible").then((r) => setOn(!!r.data.enabled)).catch(() => {});
  }, [enabled, setOn]);
  const toggle = async (next) => {
    setBusy(true);
    try {
      await api.post("/presence/invisible", { enabled: next });
      setOn(next);
      toast.success(next ? "Invisible Mode on — the pack sees you as offline" : "Invisible Mode off — your real status is visible again");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change Invisible Mode."); }
    finally { setBusy(false); }
  };
  return { on, busy, toggle };
}

// The three availability kinds a wolf can attach to a custom status, each color-coded.
const AVAIL = [
  { presence: "online", label: "Available", dot: "bg-emerald-400", ring: "peer-checked:ring-emerald-400" },
  { presence: "away", label: "Away", dot: "bg-amber-400", ring: "peer-checked:ring-amber-400" },
  { presence: "busy", label: "Do Not Disturb", dot: "bg-red-500", ring: "peer-checked:ring-red-500" },
];

export function PresenceSwitcher({ open, onToggle, onClose, connected, myPresence, myStatus, setPresence }) {
  const { user } = useAuth();
  const inv = useInvisibleMode(!!user?.is_admin);
  const [customStatus, setCustomStatus] = useState("");
  const [availKind, setAvailKind] = useState("online");
  const pm = presenceMeta(myPresence);
  const submitCustom = (e) => {
    e.preventDefault();
    if (customStatus.trim()) {
      setPresence(availKind, customStatus.trim());
      setCustomStatus("");
      onClose();
    }
  };
  return (
    <div className="relative">
      <button
        data-testid="presence-switcher"
        onClick={onToggle}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full hover:bg-slate-800/70 transition-colors"
      >
        {inv.on ? (
          <>
            <span className={`w-2.5 h-2.5 rounded-full status-dot ${pm.color}`} data-testid="invisible-indicator" title="Invisible Mode is on — you appear offline" />
            <span className="hidden sm:inline text-xs font-medium text-slate-400">Invisible</span>
          </>
        ) : (
          <>
            <span className={`w-2.5 h-2.5 rounded-full status-dot ${pm.color} ${connected ? "" : "opacity-50"}`} />
            <span className={`hidden sm:inline text-xs font-medium max-w-[110px] truncate ${pm.text}`}>{myStatus || pm.label}</span>
          </>
        )}
        <ChevronDown className="w-3 h-3 text-slate-500" />
      </button>
      {open && (
        <div className="fixed top-[calc(4rem+var(--hdr-top))] right-2 w-60 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-5.5rem-var(--hdr-top))] overflow-y-auto no-scrollbar bg-[#0d141e] border border-slate-700 rounded-xl shadow-2xl animate-slide-up z-50 sm:absolute sm:top-auto sm:right-0 sm:mt-2 sm:max-h-none" data-testid="presence-menu">
          {user?.is_admin && (
            <label className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-800 cursor-pointer" data-testid="invisible-mode-row">
              <EyeOff className="w-4 h-4 text-slate-400 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-slate-200">Invisible Mode</span>
                <span className="block text-[11px] text-slate-500">{inv.on ? "You appear offline to everyone" : "Appear offline everywhere"}</span>
              </span>
              <Switch checked={inv.on} disabled={inv.busy} onCheckedChange={inv.toggle} data-testid="invisible-mode-toggle" />
            </label>
          )}
          <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500">Set your status</div>
          {STATUS_PRESETS.map((s) => {
            const meta = presenceMeta(s.presence);
            return (
              <button
                key={s.label}
                data-testid={`status-option-${s.presence}`}
                onClick={() => { setPresence(s.presence, s.label); onClose(); }}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-slate-800/60 transition-colors ${myStatus === s.label ? "text-slate-100" : "text-slate-400"}`}
              >
                <span className={`w-2.5 h-2.5 rounded-full status-dot ${meta.color}`} />
                {s.label}
                {myStatus === s.label && <span className="ml-auto text-sky-400">✓</span>}
              </button>
            );
          })}
          <form onSubmit={submitCustom} className="p-3 border-t border-slate-800 space-y-2.5">
            <div className="flex items-center gap-2">
              <Moon className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <input
                data-testid="custom-status-input"
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
                placeholder="Custom status..."
                maxLength={40}
                className="flex-1 min-w-0 bg-slate-900/70 border border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500/50"
              />
            </div>
            <div className="flex items-center gap-1.5" data-testid="availability-picker">
              {AVAIL.map((a) => (
                <button
                  key={a.presence}
                  type="button"
                  data-testid={`availability-${a.presence}`}
                  onClick={() => setAvailKind(a.presence)}
                  title={a.label}
                  className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] font-medium transition-colors ${availKind === a.presence ? "border-slate-500 bg-slate-800/70 text-slate-100" : "border-slate-800 text-slate-500 hover:text-slate-300"}`}
                >
                  <span className={`w-2 h-2 rounded-full ${a.dot}`} />
                  <span className="hidden sm:inline truncate">{a.presence === "busy" ? "DND" : a.label}</span>
                </button>
              ))}
            </div>
            <button type="submit" data-testid="custom-status-save" className="w-full text-xs font-semibold text-sky-300 hover:text-sky-200 border border-sky-500/30 hover:border-sky-500/50 rounded-lg py-1.5 transition-colors">
              Set status
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
